import ListMenu from '@rio-cloud/rio-uikit/components/listMenu/ListMenu';
import { ListMenuItem } from '@rio-cloud/rio-uikit/components/listMenu/ListMenuGroup';
import { MouseEventHandler, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import {useLocation, useNavigate} from "react-router-dom";

import { ABOUT_NAVIGATION_ACTIONS,ABOUT_NAVIGATION_CATEGORY, gaPush } from '@/configuration/setup/googleAnalytics';
import { selectCurrentPage, setCurrentPage } from '@/store/about/aboutSlice';
import { Page, pages, pageToContent } from '@/store/about/pageToContent';
import { closeSidebar } from '@/store/sidebar/sidebarSlice';

const AboutNavigation = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activePage = useSelector(selectCurrentPage);
  const title = intl.formatMessage({ id: 'common.gettingStarted.label' });
  const isSmallScreen = useMediaQuery({ query: '(max-width: 47.5rem)' });

  const validTitles = new Set(Object.values(pageToContent).map((entry) => entry.title));

  const getPageKeyByTitle = (title: string): keyof typeof pageToContent | undefined => {
    const entry = Object.entries(pageToContent).find(
        ([_, value]) => value.title === title
    );
    return entry ? entry[0] as keyof typeof pageToContent : undefined;
  };

  const useGetStartedParam = () => {
    const { search, pathname } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const paramKey = search.startsWith("?") ? search.slice(1).split("=")[0] : '';

    function handleAboutPage(paramKey: string | undefined) {
      const isParamValid = paramKey && validTitles.has(paramKey);

      if (!isParamValid) {
        const defaultTitle = pageToContent.page1.title;
        navigate(`/about?${defaultTitle}`, { replace: true });
        return;
      }

      const key = getPageKeyByTitle(paramKey!);
      if (key) dispatch(setCurrentPage(key));
    }

    useEffect(() => {
      if (pathname === "/about") handleAboutPage(paramKey);
    }, [paramKey, pathname]);

    return validTitles.has(paramKey) ? paramKey : 'welcome';
  };

  const useUpdateGetStartedParamFromPageKey = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    return (pageKey: keyof typeof pageToContent) => {
      const title = pageToContent[pageKey]?.title;
      if (title) {
        navigate(`${pathname}?${title}`, { replace: true });
      }
    };
  };

  const updateParam = useUpdateGetStartedParamFromPageKey();

  useGetStartedParam();

  const handleItemClick = (page: Page) => () => {
    gaPush({
      category: ABOUT_NAVIGATION_CATEGORY,
      action: ABOUT_NAVIGATION_ACTIONS.CLICK_PAGE,
      label: `Clicked page: ${page}`,
    });

    updateParam(page);

    dispatch(setCurrentPage(page));

    if (isSmallScreen) {
      dispatch(closeSidebar());
    }
  };

  const menuItems = (
    activeItem: Page,
    onItemClick: (page: Page) => MouseEventHandler<HTMLSpanElement>,
  ): ListMenuItem[] => [
    {
      group: title,
      navItems: pages.map((page) => ({
        key: page,
        item: (
          <span className={activeItem === page ? 'active' : ''} data-key={page} onClick={onItemClick(page)}>
            {intl.formatMessage({ id: pageToContent[page].translation })}
          </span>
        ),
      })),
    },
  ];

  useEffect(() => {
    if (isSmallScreen && activePage === 'page1') {
      dispatch(closeSidebar());
    }
  }, [activePage, dispatch]);

  return <ListMenu responsive={false} menuItems={menuItems(activePage, handleItemClick)} className="margin-top-20" />;
};

export default AboutNavigation;
